 
/* eslint-disable @typescript-eslint/no-unused-vars */
 
 
 
 
 
import { MARKET_SEGMENTS, MARKET_CONSTANTS, TIME_CONSTANTS } from '../../config/gameConstants';
import type { Product } from '@smartphone-tycoon/shared-core';

interface MarketCapacityData {
  segmentName: string;
  totalCapacity: number;
  annualCapacity: number;
  soldToDate: number;
  remainingCapacity: number;
  utilizationRate: number;
}

interface YearlyCapacityData {
  year: number;
  segmentCapacities: Map<string, number>;
}

export class MarketCapacityService {
  private static instance: MarketCapacityService;
  private yearlyCapacities: Map<number, YearlyCapacityData> = new Map();
  
  private constructor() {}

  public static getInstance(): MarketCapacityService {
    if (!MarketCapacityService.instance) {
      MarketCapacityService.instance = new MarketCapacityService();
    }
    return MarketCapacityService.instance;
  }

  /**
   * Get the total market capacity for a segment for a given year
   */
  public getAnnualCapacity(segmentName: string, year: number): number {
    const baseSize = this.getSegmentBaseSize(segmentName);
    const annualPurchaseRate = MARKET_CONSTANTS.annualPhonePurchaseRate;
    const growthRate = this.calculateGrowthRate(year);
    
    return Math.floor(baseSize * annualPurchaseRate * growthRate);
  }

  /**
   * Track sales and update remaining capacity
   */
  public recordSales(segmentName: string, unitsSold: number, year: number): boolean {
    const yearData = this.getOrCreateYearData(year);
    const currentSold = yearData.segmentCapacities.get(segmentName) || 0;
    const annualCapacity = this.getAnnualCapacity(segmentName, year);
    
    // Check if sales would exceed capacity
    if (currentSold + unitsSold > annualCapacity) {
      return false; // Sale would exceed capacity
    }
    
    yearData.segmentCapacities.set(segmentName, currentSold + unitsSold);
    return true;
  }

  /**
   * Get remaining capacity for a segment in a year
   */
  public getRemainingCapacity(segmentName: string, year: number): number {
    const yearData = this.getOrCreateYearData(year);
    const currentSold = yearData.segmentCapacities.get(segmentName) || 0;
    const annualCapacity = this.getAnnualCapacity(segmentName, year);
    
    return Math.max(0, annualCapacity - currentSold);
  }

  /**
   * Get market capacity data for all segments
   */
  public getMarketCapacityData(year: number): MarketCapacityData[] {
    const segments = Object.keys(MARKET_SEGMENTS.names);
    const yearData = this.getOrCreateYearData(year);
    
    return segments.map(segmentKey => {
      const segmentName = MARKET_SEGMENTS.names[segmentKey];
      const totalCapacity = this.getSegmentBaseSize(segmentName);
      const annualCapacity = this.getAnnualCapacity(segmentName, year);
      const soldToDate = yearData.segmentCapacities.get(segmentName) || 0;
      const remainingCapacity = Math.max(0, annualCapacity - soldToDate);
      const utilizationRate = annualCapacity > 0 ? soldToDate / annualCapacity : 0;
      
      return {
        segmentName,
        totalCapacity,
        annualCapacity,
        soldToDate,
        remainingCapacity,
        utilizationRate
      };
    });
  }

  /**
   * Calculate constrained daily sales based on remaining capacity
   */
  public constrainDailySales(
    segmentName: string,
    desiredDailySales: number,
    currentDate: { day: number; month: number; year: number }
  ): number {
    const remainingCapacity = this.getRemainingCapacity(segmentName, currentDate.year);
    const daysLeftInYear = this.calculateDaysLeftInYear(currentDate);
    
    // If no capacity left, return 0
    if (remainingCapacity <= 0) {
      return 0;
    }
    
    // If desired sales would consume all remaining capacity too quickly,
    // spread it out over remaining days
    const maxDailySales = Math.ceil(remainingCapacity / Math.max(1, daysLeftInYear));
    
    return Math.min(desiredDailySales, maxDailySales, remainingCapacity);
  }

  /**
   * Reset capacity tracking for a new year
   */
  public resetYearCapacity(year: number): void {
    this.yearlyCapacities.delete(year);
  }

  private getOrCreateYearData(year: number): YearlyCapacityData {
    let yearData = this.yearlyCapacities.get(year);
    if (!yearData) {
      yearData = {
        year,
        segmentCapacities: new Map()
      };
      this.yearlyCapacities.set(year, yearData);
    }
    return yearData;
  }

  private getSegmentBaseSize(segmentName: string): number {
    // Map segment names to sizes
    const sizeMap = {
      [MARKET_SEGMENTS.names.BUDGET]: MARKET_SEGMENTS.sizes.budget,
      [MARKET_SEGMENTS.names.BUDGET_CONSCIOUS]: MARKET_SEGMENTS.sizes.budget,
      [MARKET_SEGMENTS.names.MAINSTREAM]: MARKET_SEGMENTS.sizes.mainstream,
      [MARKET_SEGMENTS.names.MAINSTREAM_USERS]: MARKET_SEGMENTS.sizes.mainstream,
      [MARKET_SEGMENTS.names.PREMIUM]: MARKET_SEGMENTS.sizes.premium,
      [MARKET_SEGMENTS.names.PREMIUM_ENTHUSIASTS]: MARKET_SEGMENTS.sizes.premium,
      [MARKET_SEGMENTS.names.ENTERPRISE]: MARKET_SEGMENTS.sizes.enterprise,
      [MARKET_SEGMENTS.names.ENTERPRISE_USERS]: MARKET_SEGMENTS.sizes.enterprise
    };
    
    return sizeMap[segmentName] || MARKET_SEGMENTS.sizes.mainstream;
  }

  private calculateGrowthRate(year: number): number {
    // Dynamic growth model based on market maturity
    const baseYear = 2000;
    const yearsDiff = year - baseYear;
    
    if (year < 2007) {
      // Pre-smartphone era: slow growth (3% annually)
      return Math.pow(1.03, yearsDiff);
    } else if (year < 2010) {
      // Smartphone introduction: rapid growth (15% annually)
      const priorGrowth = Math.pow(1.03, 7); // 2000-2007
      const rapidYears = year - 2007;
      return priorGrowth * Math.pow(1.15, rapidYears);
    } else if (year < 2015) {
      // Mainstream adoption: strong growth (10% annually)
      const priorGrowth = Math.pow(1.03, 7) * Math.pow(1.15, 3); // 2000-2010
      const strongYears = year - 2010;
      return priorGrowth * Math.pow(1.10, strongYears);
    } else {
      // Market maturity: moderate growth (5% annually)
      const priorGrowth = Math.pow(1.03, 7) * Math.pow(1.15, 3) * Math.pow(1.10, 5); // 2000-2015
      const matureYears = year - 2015;
      return priorGrowth * Math.pow(1.05, matureYears);
    }
  }

  private calculateDaysLeftInYear(currentDate: { day: number; month: number; year: number }): number {
    const currentDayOfYear = (currentDate.month - 1) * TIME_CONSTANTS.daysPerMonth + currentDate.day;
    return Math.max(1, TIME_CONSTANTS.daysPerYear - currentDayOfYear);
  }

  /**
   * Get total market saturation across all segments
   */
  public getMarketSaturation(year: number): number {
    const capacityData = this.getMarketCapacityData(year);
    const totalAnnualCapacity = capacityData.reduce((sum, data) => sum + data.annualCapacity, 0);
    const totalSold = capacityData.reduce((sum, data) => sum + data.soldToDate, 0);
    
    return totalAnnualCapacity > 0 ? totalSold / totalAnnualCapacity : 0;
  }

  /**
   * Reset all capacity tracking data
   */
  public reset(): void {
    this.yearlyCapacities.clear();
  }
}